<!-- /src/views/Session.vue -->
<template>
  <LayoutFullWidth class="layout--session layout--header--has-interactions">
    <template v-if="post" v-slot:header>
      <h1
        class="page-header"
        v-html="post.title.rendered"
      ></h1>
      <PostInteractions :post="post" />
    </template>
    <template v-slot:content>
      <div v-if="post !== 'undefined'"
        class="layout--session--container layout--session--additional-content"
        :class="{'layout--session--additional-content': additionalElements}"
      >
        <div class="body-content">
          <div v-if="(showImage && !hasMedia && isDesktop)" class="session__image">
            <img :src="post.featured_media.post" alt="" />
          </div>
          <div v-if="hasMedia" class="session__media-element">
            <div v-if="isAudio && isDesktop" class="session__image">
              <img :src="post.featured_media.post" alt="" />
            </div>
            <MediaPlayer :mediaID="mediaID" :key="mediaID" :post="post" :startTime="startTime" @mediaComplete="markComplete" />
            <TableOfContents :post="post" @jumpTo="updateStartTime" />
          </div>
          <div class="session__body-content">
            <div v-if="bodyContent" v-html="bodyContent"></div>     
          </div>
        </div>
        <div v-if="additionalElements"
          class="additional-elements">
          <div v-if="downloadableFiles.length > 0" class="session__attachments">
            <h2 class="session__attachments__title">Downloadable Items</h2>
            <div v-if="downloadableFiles.length > 0" class="downloadable-items">
              
              <Downloadable
                v-for="(file, index) in downloadableFiles"
                :key="`attachment_ind_${index}`"
                :file="file"
              />
            </div>
          </div>
          <div v-if="secureFiles.length > 0" class="session__attachments">
            <h2 class="session__attachments__title">Viewable Attachments</h2>
            <div v-if="secureFiles.length > 0" class="secure-items">
              
              <Downloadable
                v-for="(file, index) in secureFiles"
                :key="`secure_ind_${index}`"
                :file="file"
                :isSecure="true"
              />
            </div>
          </div>
          <div v-if="relatedContent"
            class="session__related-content--container"
          >
            <h2 class="session__attachments__title">Related Content</h2>
            <ul class="session__related-content--container__items">
              <li class="session__related-content--container__item"
                v-for="(related, index) in relatedContent"
                :key="index"
              >
                <Card :post="related" :type="type" :cardSize="'small'" />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Loader v-else class="session__body-content" color="gray" />
    </template>
  </LayoutFullWidth>
</template>

<script>

import _isUndefined from 'lodash/isUndefined'
import PostInteractions from '@/components/common/PostInteractions'
import LayoutFullWidth from '@/components/layouts/FullWidth'
import MediaPlayer from '@/components/Session/Media'
// import SessionSidebar from '@/components/Session/Sidebar'
import Downloadable from '@/components/common/Downloadable'
import Loader from '@/components/common/Loaders/Loader'
import TableOfContents from '@/components/MediaElements/TableOfContents'
import secondsConversions from '@/components/common/utils/filters/secondConversions'
import Card from '@/components/common/Card/Card'
import postMixin from '@/mixins/postMixin'
import resizeMixin from '@/mixins/resizeMixin'
import { deepSearch, checkNested } from '@/components/common/utils/helpers.js'

export default {
  mixins: [postMixin, resizeMixin],

  data() {
    return {
      tocVisible: false,
      startTime: 0,
      completeLoading: false,
    }
  },

  computed: {
    post() {
      const output = this.$store.getters['posts/findItemByProp'](
        'sessions',
        this.$route.params.slug,
        'slug',
      )
      // console.log(output)

      if (output) this.$store.dispatch('updatePageTitle', 'Session: ' + output.title.rendered)
      return output
    },

    showImage() {
      const { post } = this
      return checkNested(post, 'featured_media') && typeof post.featured_media.post === 'string' && post.featured_media.post.indexOf("no-image.png") == -1
    },

    mediaID() {
      const { post, isVideo, isAudio } = this
      const {acf} = post
      if (isVideo) return acf.video_id || null
      if (isAudio && acf.audio_file) return acf.audio_file.ID || null
      return null
    },

    audioID() {
      const { post, isAudio } = this
      if (isAudio && post.acf && post.acf.audio_file && post.acf.audio_file.ID) {
        return post.acf.audio_file.ID
      }
      return false
    },

    bodyContent() {
      let output = false
      if (checkNested(this.post, 'acf', 'section_content')) {
        const { post } = this
        const { acf } = post
        const { section_content } = acf
        if (section_content && section_content.length > 0) {
          output = ''
          section_content.forEach(section => {
            output += `${section.title}${section.content}`
          })
        }
      }
      return output
    },

    isVideo() {
      if (this.post.session_type[0] === 'video') {
        return true
      }
      return false
    },

    isAudio() {
      if (this.post.session_type[0] === 'audio') {
        return true
      }
      return false
    },

    hasMedia() {
      if (checkNested(this.post, 'session_type') && 
      (this.post.session_type[0] === 'video' ||
        this.post.session_type[0] === 'audio')
      ) {
        return true
      }
      return false
    },

    downloadableFiles() {
      const { post } = this
      const files = []
      let downloadableFiles = deepSearch(post, ['acf', 'downloadable_files'])
      if (Array.isArray(downloadableFiles)) {
        downloadableFiles.forEach(fileObj => {
          files.push(fileObj['file'])
        })
        return files
      }
      return false
    },

    secureFiles() {
      const { post } = this
      const files = []
      let secureFiles = deepSearch(post, ['acf', 'secure_files'])
      if (Array.isArray(secureFiles)) {
        secureFiles.forEach(fileObj => {
          files.push(fileObj['secure_file'])
        })
        return files
      }
      return false
    },

    additionalElements() {
      const { secureFiles, downloadableFiles, relatedContent } = this
      return downloadableFiles || relatedContent || secureFiles
    },

    mediaOnly() {
      const { bodyContent, hasMedia, secureFiles, downloadableFiles, relatedContent } = this
      return !(bodyContent || downloadableFiles || secureFiles || relatedContent) && hasMedia
    },
  },

  filters: {
    ...secondsConversions,
  },

  methods: {
    updateStartTime(event) {
      // console.log({event})
      this.startTime = event
    },

    jumpToTime(time) {
      // console.log({time})

      this.startTime = time
    },

    checkForCurrentTimestamp() {
      if (!_isUndefined(this.currentInteraction)) {
        if ( this.currentInteraction.mediaType == 'video' || this.currentInteraction.mediaType == 'audio' ) {
          this.jumpToTime(this.currentInteraction.timestamp)
        }
      } else {
        setTimeout(() => {
          this.checkForCurrentTimestamp()
        }, 500)
      }
    },

    createPDF(base64) {
      const blob = this.base64ToBlob( base64, 'application/pdf' );
      const url = URL.createObjectURL( blob );
      const pdfWindow = window.open("")

      pdfWindow.addEventListener('contextmenu', e => {
        e.preventDefault()
      }, false)
      pdfWindow.document.write("<object id='pdfView' width='100%' height='100%' data='" + url + "#toolbar=0' border='0' oncontextmenu='javascript:alert(\'success!\');return false;'></object>");
      // pdfWindow.document.write("<canvas id='pdfView' width='100%' height='100%' border='0'></canvas>");
      // const canvas = pdfWindow.document.getElementById("pdfView")
      const pdfBody = pdfWindow.document.getElementsByTagName("body")[0];
      pdfBody.style.margin = 0


      const pdfviewer = pdfWindow.document.getElementById("pdfView")
      const pdfB = pdfviewer.contentWindow.document.getElementsByTagName("body")[0]
      pdfB.addEventListener('contextmenu', e => {
        e.preventDefault()
      }, false)

      pdfB.onkeypress = (evt) => {
            console.log(evt)
            if(evt.button == 2){
              return false;    
          }
      }

      // setTimeout( () => {
      // }, 0)
      // iframe.addEventListener('contextmenu', e => {
      //   e.preventDefault()
      // }, false)
      // iframe.contentWindow.addEventListener('contextmenu', e => {
      //   e.preventDefault()
      // }, false)
      // iframe.contentWindow.document.addEventListener('contextmenu', e => {
      //   e.preventDefault()
      // }, false)
      //   console.log(iframe.contentWindow)

    },

    base64ToBlob( base64, type = "application/octet-stream" ) {
      const binStr = atob( base64 );
      const len = binStr.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        arr[ i ] = binStr.charCodeAt( i );
      }
      return new Blob( [ arr ], { type: type } );
    },

  },

  beforeDestroy() {
    // this.post = {}
  },

  components: {
    LayoutFullWidth,
    Loader,
    MediaPlayer,
    Downloadable,
    TableOfContents,
    PostInteractions,
    Card,
  },

  mounted() {
    this.visitedPost()
    this.checkForCurrentTimestamp()
    // document.title = this.post.title.rendered

  },

  beforeMount() {
    // this.$store.dispatch('posts/getPost', { slug: this.$route.params.slug, title: this.$route.meta.title })

    this.$store.dispatch('updateBackgroundImage', false)
    this.$store.dispatch('updateBackgroundDark', false)
  },

}
</script>

<style scoped>

.page-header {
  margin: 0;
}

.layout--session {
  &--container {
    display: grid;
    align-items: start;
  }

  &--additional-content,
  &--split-body {
    grid-template-columns: 1fr;
    grid-gap: 2rem;
    display: grid;
    @media (--desktop) {
      grid-template-columns: 7fr 3fr;
    }
  }

  &--media-only {
    width: calc(60% - 1rem);
    /* margin: 0 auto; */
  }

  .body-content {
    display: grid;
    grid-gap: 2rem;

  }

  .session {
    &__content {
      border-radius: var(--borderRadiusSmall);
      /* overflow: hidden; */

      @media (--tablet) {
        border-radius: 0;
      }
    }


    &__body-content {
      /* padding: var(--itemSpacing) 0 0 0; */
      /* margin-top: 2rem; */
      width: 100%;
      /* max-width: calc(100% - 2rem); */

      a {
        text-decoration: underline;
      }

      ol {
        list-style: decimal;
        margin-left: 1rem;

        li {
          ol {
            list-style: lower-alpha;
            margin-left: 1.5rem;
          }
        }
      }

      ul {
        margin-left: 1rem;
        list-style-type: square;
      }

      blockquote {
        border-left: 5px solid var(--gray);
        padding-left: 1rem;
      }
    }

    &__attachments {
      &__title {
        color: var(--gray);
        font-weight: var(--fontWeightBold);
        font-size: 0.8em;
        text-transform: uppercase;
        margin: 0 0 0.5rem;
      }
    }

    &__related-content {
      &--container {
        margin-bottom: 3rem;
        &__items {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
          grid-gap: 1rem;
        }
      }
    }
  }
}

.additional-elements {
  > div {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0
    }
  }
}


.downloadable-items {
  display: grid;
  grid-gap: .5rem;
}

</style>
